import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { CallsSidebarComponent } from '../calls-sidebar/calls-sidebar.component';
import { CallsInCallComponent } from '../calls-in-call/calls-in-call.component';
import { CallRoom, type CallRoomParticipant } from './calls-types';
import { CallsService } from './calls.service';
import { ApiService } from 'app/shared/apis/api.service';
import { AuthService } from 'app/core/auth/auth.service';
import { SocketService } from 'app/services/socket.service';
import { ConversationService } from 'app/services/conversation.service';
import { CompanyService } from 'app/services/company.service';
import { Conversation, ConversationContact } from 'app/models/conversation.model';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

export type { CallRoom, CallRoomParticipant, OnlineUser } from './calls-types';

@Component({
    selector: 'app-calls-widget',
    standalone: true,
    imports: [
        CommonModule,
        MatBadgeModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        CallsSidebarComponent,
        CallsInCallComponent,
        AutoTranslatePipe,
    ],
    templateUrl: './calls-widget.component.html',
    styleUrls: ['./calls-widget.component.scss'],
})
export class CallsWidgetComponent implements OnInit, OnDestroy {
    sidebarOpen = false;
    callFullscreen = false;
    activeRoom: CallRoom | null = null;
    rooms: CallRoom[] = [];
    conversations: Conversation[] = [];
    contacts: ConversationContact[] = [];
    activeConversation: Conversation | null = null;
    currentUserId: number | null = null;
    currentUserName: string | null = null;
    companyId: number | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly callsService: CallsService,
        private readonly conversationService: ConversationService,
        private readonly companyService: CompanyService,
        private readonly toastr: ToastrService,
        private readonly socketService: SocketService,
        private readonly authService: AuthService,
    ) {}

    ngOnInit(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            this.currentUserId = user?.user_id ?? null;
            this.currentUserName = user?.name ?? null;
            this.conversationService.setCurrentUserId(this.currentUserId);
            this.ensurePresenceIdentify(user?.user_id);
            this.joinUserRoom();
        });
        this.companyId = this.companyService.getCurrentCompany()?.company_id ?? null;
        this.companyService.currentCompany$.pipe(takeUntil(this.destroy$)).subscribe((c) => {
            this.companyId = c?.company_id ?? null;
            this.refreshConversations();
        });
        this.conversationService.conversations$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
            this.conversations = items;
        });
        this.callsService.enterRoomRequested$.pipe(takeUntil(this.destroy$)).subscribe((room) => {
            this.onEnterRoom(room);
        });
        this.socketService
            .fromEvent<void>('connect')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.ensurePresenceIdentify(this.currentUserId);
                this.joinUserRoom();
            });
        if (this.socketService.isConnected()) {
            this.joinUserRoom();
        }
        this.refreshConversations();
        this.callsService.getRooms().subscribe({
            next: (rooms) => {
                this.rooms = rooms;
            },
            error: () => {
                this.rooms = [];
            },
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.leaveActiveRoomBeacon();
        this.conversationService.leaveConversation();
    }

    get totalUnread(): number {
        return (this.conversations || []).reduce((sum, c) => sum + (Number(c.unread_count) || 0), 0);
    }

    @HostListener('window:pagehide')
    @HostListener('window:beforeunload')
    onPageUnload(): void {
        this.leaveActiveRoomBeacon();
    }

    openSidebar(): void {
        if (!this.activeRoom) {
            this.sidebarOpen = true;
            this.refreshSidebarData();
            ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
                this.currentUserId = user?.user_id ?? null;
                this.currentUserName = user?.name ?? null;
                this.conversationService.setCurrentUserId(this.currentUserId);
                this.ensurePresenceIdentify(user?.user_id);
                this.joinUserRoom();
                if (user?.user_id && this.socketService.isConnected()) {
                    setTimeout(() => this.refreshSidebarData(), 500);
                }
            });
        }
    }

    closeSidebar(): void {
        this.sidebarOpen = false;
        if (!this.activeRoom) {
            this.activeConversation = null;
            this.conversationService.leaveConversation();
        }
    }

    openInCallSidebar(): void {
        if (!this.activeRoom || !this.callFullscreen) return;
        if (this.sidebarOpen) {
            this.closeSidebar();
            return;
        }
        this.sidebarOpen = true;
        this.refreshSidebarData();
        const linked = this.conversationForActiveRoom();
        if (linked) {
            this.activeConversation = linked;
        }
    }

    onCallFullscreenChange(isFullscreen: boolean): void {
        this.callFullscreen = isFullscreen;
        if (!isFullscreen) {
            this.sidebarOpen = false;
            this.activeConversation = null;
            this.conversationService.leaveConversation();
        }
    }

    onOpenConversation(conversation: Conversation): void {
        this.activeConversation = conversation;
    }

    onBackToList(): void {
        this.activeConversation = null;
        this.conversationService.leaveConversation();
        this.refreshConversations();
    }

    onStartDirectChat(contact: ConversationContact): void {
        if (!this.companyId) {
            this.toastr.warning('Selecione uma empresa');
            return;
        }
        this.conversationService.createDirect(this.companyId, contact.user_id).subscribe({
            next: (conv) => {
                this.refreshConversations();
                this.activeConversation = conv;
            },
            error: () => this.toastr.error('Não foi possível iniciar a conversa'),
        });
    }

    onGroupCreated(conversation: Conversation): void {
        this.refreshConversations();
        this.activeConversation = conversation;
    }

    onGroupUpdated(conversation: Conversation): void {
        this.patchConversationInList(conversation);
        if (this.activeConversation?.conversation_id === conversation.conversation_id) {
            this.activeConversation = { ...this.activeConversation, ...conversation };
        }
        this.refreshConversations();
    }

    onGroupDeleted(conversationId: number): void {
        if (this.activeConversation?.conversation_id === conversationId) {
            this.activeConversation = null;
            this.conversationService.leaveConversation();
        }
        this.refreshConversations();
    }

    onEnterVideoFromConversation(conversation: Conversation): void {
        if (!conversation?.conversation_id) {
            this.toastr.warning('Sala de vídeo não disponível');
            return;
        }

        this.conversationService.startCallRoom(conversation.conversation_id).subscribe({
            next: (updated) => {
                const merged: Conversation = {
                    ...conversation,
                    ...updated,
                    call_room_id: updated.call_room_id ?? conversation.call_room_id,
                    call_room_slug: updated.call_room_slug ?? conversation.call_room_slug,
                };
                this.activeConversation = merged;
                this.patchConversationInList(merged);

                const slug = merged.call_room_slug;
                const id = merged.call_room_id;
                if (!slug && !id) {
                    this.toastr.warning('Sala de vídeo não disponível');
                    return;
                }

                const room: CallRoom = {
                    id: String(id ?? slug),
                    slug: slug ?? undefined,
                    name: merged.display_name || merged.name || 'Chamada',
                    status: 'AVAILABLE',
                    participants: (merged.participants || []).map((p) => ({
                        user_id: p.user_id,
                        name: p.name,
                        initials: (p.name || '?').slice(0, 2).toUpperCase(),
                    })),
                    onlineCount: 0,
                };
                this.onEnterRoom(room);
            },
            error: () => this.toastr.error('Não foi possível iniciar a chamada'),
        });
    }

    private patchConversationInList(conversation: Conversation): void {
        const idx = this.conversations.findIndex((c) => c.conversation_id === conversation.conversation_id);
        if (idx >= 0) {
            this.conversations = [
                ...this.conversations.slice(0, idx),
                { ...this.conversations[idx], ...conversation },
                ...this.conversations.slice(idx + 1),
            ];
        }
    }

    onEnterRoom(room: CallRoom): void {
        const idOrSlug = room.slug ?? room.id;
        this.ensurePresenceIdentify(this.currentUserId);
        this.callsService.joinRoom(idOrSlug).subscribe({
            next: () => {
                this.callsService.getRooms().subscribe({
                    next: (rooms) => {
                        this.rooms = rooms;
                        const fresh = rooms.find((r) => r.id === room.id || r.slug === room.slug) ?? room;
                        this.activeRoom = fresh;
                        this.sidebarOpen = false;
                        this.activeConversation = null;
                    },
                    error: () => {
                        this.activeRoom = room;
                        this.sidebarOpen = false;
                    },
                });
            },
            error: () => {},
        });
    }

    private ensurePresenceIdentify(userId: number | null | undefined): void {
        if (!userId) return;
        if (this.socketService.isConnected()) {
            this.socketService.emit('user_identify', { user_id: userId });
        }
    }

    private joinUserRoom(): void {
        if (!this.currentUserId || !this.socketService.isConnected()) return;
        this.socketService.emit('joinUser', this.currentUserId);
    }

    onLeaveCall(): void {
        const room = this.activeRoom;
        const idOrSlug = room?.slug ?? room?.id;
        const conversation = this.conversationForActiveRoom() ?? this.activeConversation;
        this.activeRoom = null;
        this.callFullscreen = false;
        this.sidebarOpen = false;
        if (idOrSlug) {
            this.callsService.leaveRoom(String(idOrSlug)).subscribe({
                next: (res) => {
                    if (res?.roomFinished) {
                        this.toastr.info('Chamada finalizada');
                        this.clearConversationCallRoom(conversation);
                    }
                    this.refreshRoomsQuietly();
                },
                error: () => {
                    this.refreshRoomsQuietly();
                },
            });
        }
    }

    private clearConversationCallRoom(conversation: Conversation | null): void {
        if (!conversation?.conversation_id) return;
        const cleared: Conversation = {
            ...conversation,
            call_room_id: null,
            call_room_slug: null,
        };
        if (this.activeConversation?.conversation_id === conversation.conversation_id) {
            this.activeConversation = cleared;
        }
        this.patchConversationInList(cleared);
    }

    get roomsForSidebar(): CallRoom[] {
        return this.rooms.filter((r) => r.status === 'AVAILABLE' || r.status === 'IN_CALL');
    }

    get inCallParticipants(): CallRoomParticipant[] {
        if (!this.activeRoom) return [];
        const list = this.activeRoom.participants || [];
        const meId = this.currentUserId;
        return list
            .filter((_, i) => i < 5)
            .map((p) =>
                meId != null && p.user_id === meId
                    ? { ...p, name: 'Você', initials: p.initials || 'V' }
                    : p,
            );
    }

    private refreshSidebarData(): void {
        this.callsService.getRooms().subscribe({
            next: (rooms) => {
                this.rooms = rooms;
            },
            error: () => {
                this.rooms = [];
            },
        });
        this.refreshConversations();
        if (this.companyId) {
            this.conversationService.listContacts(this.companyId).subscribe({
                next: (contacts) => {
                    this.contacts = contacts;
                },
                error: () => {
                    this.contacts = [];
                },
            });
        }
    }

    private conversationForActiveRoom(): Conversation | null {
        const room = this.activeRoom;
        if (!room) return null;
        return (
            this.conversations.find(
                (c) =>
                    (c.call_room_id != null && String(c.call_room_id) === String(room.id)) ||
                    (c.call_room_slug && room.slug && c.call_room_slug === room.slug),
            ) ?? null
        );
    }

    private refreshConversations(): void {
        this.conversationService.list(this.companyId ?? undefined).subscribe({
            error: () => {
                this.conversations = [];
            },
        });
    }

    private refreshRoomsQuietly(): void {
        this.callsService.getRooms().subscribe({
            next: (rooms) => {
                this.rooms = rooms;
            },
            error: () => {},
        });
    }

    private leaveActiveRoomBeacon(): void {
        const room = this.activeRoom;
        if (!room) return;
        const idOrSlug = room.slug ?? room.id;
        const token = this.authService.token;
        this.activeRoom = null;
        if (!idOrSlug || !token) return;
        try {
            fetch(`${environment.apiUrl}/call/rooms/${encodeURIComponent(String(idOrSlug))}/leave`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: '{}',
                keepalive: true,
            }).catch(() => {});
        } catch {
            // ignore
        }
    }
}
